import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_NAME = 'Promokod.online';
const SITE_URL = 'https://promokod.online';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;
const MONTHS_RU = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
];

export interface SeoOptions {
  title: string;
  description: string;
  canonicalPath?: string;
  noIndex?: boolean;
  ogType?: 'website' | 'article';
}

export function buildCanonicalUrl(pathname: string): string {
  const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return normalizedPath === '/' ? `${SITE_URL}/` : `${SITE_URL}${normalizedPath}`;
}

export function buildRobotsValue(noIndex = false): string {
  return noIndex ? 'noindex, nofollow' : 'index, follow';
}

export function getCurrentMonthYearLabel(date = new Date()): string {
  return `${MONTHS_RU[date.getMonth()]} ${date.getFullYear()}`;
}

function ensureMetaAttribute(selector: string, attribute: 'name' | 'property', value: string) {
  let element = document.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, value);
    document.head.appendChild(element);
  }
  return element;
}

function ensureLinkRel(selector: string, rel: string) {
  let element = document.querySelector<HTMLLinkElement>(selector);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  return element;
}

export function applySeoMeta({
  title,
  description,
  canonicalPath = '/',
  noIndex = false,
  ogType = 'website',
}: SeoOptions) {
  document.title = title;

  const canonicalUrl = buildCanonicalUrl(canonicalPath);
  const robots = buildRobotsValue(noIndex);

  ensureMetaAttribute('meta[name="description"]', 'name', 'description').setAttribute('content', description);
  ensureMetaAttribute('meta[name="robots"]', 'name', 'robots').setAttribute('content', robots);
  ensureMetaAttribute('meta[property="og:title"]', 'property', 'og:title').setAttribute('content', title);
  ensureMetaAttribute('meta[property="og:description"]', 'property', 'og:description').setAttribute('content', description);
  ensureMetaAttribute('meta[property="og:type"]', 'property', 'og:type').setAttribute('content', ogType);
  ensureMetaAttribute('meta[property="og:url"]', 'property', 'og:url').setAttribute('content', canonicalUrl);
  ensureMetaAttribute('meta[property="og:image"]', 'property', 'og:image').setAttribute('content', DEFAULT_OG_IMAGE);
  ensureMetaAttribute('meta[property="twitter:card"]', 'property', 'twitter:card').setAttribute('content', 'summary_large_image');
  ensureMetaAttribute('meta[property="twitter:title"]', 'property', 'twitter:title').setAttribute('content', title);
  ensureMetaAttribute('meta[property="twitter:description"]', 'property', 'twitter:description').setAttribute('content', description);
  ensureMetaAttribute('meta[property="twitter:url"]', 'property', 'twitter:url').setAttribute('content', canonicalUrl);
  ensureMetaAttribute('meta[property="twitter:image"]', 'property', 'twitter:image').setAttribute('content', DEFAULT_OG_IMAGE);
  ensureLinkRel('link[rel="canonical"]', 'canonical').setAttribute('href', canonicalUrl);
}

export function usePageSeo(options: SeoOptions) {
  const location = useLocation();
  const {
    title,
    description,
    canonicalPath: canonicalPathOverride,
    noIndex = false,
    ogType = 'website',
  } = options;
  const canonicalPath = canonicalPathOverride ?? location.pathname;

  useEffect(() => {
    applySeoMeta({
      title,
      description,
      canonicalPath,
      noIndex,
      ogType,
    });
  }, [canonicalPath, description, noIndex, ogType, title]);
}

export function buildStoreSeo(storeName: string, category: string, promosCount: number, maxDiscount?: string) {
  const monthYear = getCurrentMonthYearLabel();
  const titleDiscount = maxDiscount && maxDiscount !== 'Акция'
    ? ` до ${maxDiscount}`
    : '';
  const title = `Промокоды ${storeName}${titleDiscount} на ${monthYear} | ${SITE_NAME}`;
  const description = promosCount > 0
    ? `Промокоды ${storeName} на ${monthYear}. Сегодня доступно ${promosCount} актуальных акций и промокодов в категории «${category}». Проверенные предложения и переходы по партнёрским ссылкам.`
    : `${storeName} в категории «${category}». Следите за актуальными акциями, промокодами и специальными предложениями на ${SITE_NAME}.`;

  return { title, description };
}

export function buildCategorySeo(categoryName: string, storesCount: number, promosCount: number) {
  const monthYear = getCurrentMonthYearLabel();
  const title = `Промокоды в категории ${categoryName} на ${monthYear} | ${SITE_NAME}`;
  const description = `${categoryName} на ${monthYear}: ${storesCount} магазинов и ${promosCount} актуальных предложений. Найдите рабочие промокоды, акции и скидки в категории «${categoryName}».`;

  return { title, description };
}
